import React, { useState, useEffect, useMemo } from 'react';
import { Download, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import NowName, { staffNow } from './shared/NowName';
import { exportToExcel } from '../utils/excelExport';
import { formatDate as toDisplayDate, formatDateShort } from '../utils/dateFormat';
import DashboardTabHeader from './shared/DashboardTabHeader';
import { PAGE_SIZE, TablePager } from './SortableTable';
import { exportExcelBtnCls } from './shared/ExportExcelButton';

const parseDMY = (str) => {
  if (!str) return null;
  const s = String(str);
  // DD-MM-YYYY
  const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (m) return new Date(`${m[3]}-${m[2].padStart(2,'0')}-${m[1].padStart(2,'0')}`);
  // D-Mon-YYYY or D-Mon-YY
  const months = { jan:1,feb:2,mar:3,apr:4,may:5,jun:6,jul:7,aug:8,sep:9,oct:10,nov:11,dec:12 };
  const m2 = s.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{2,4})$/);
  if (m2) {
    const mon = months[m2[2].toLowerCase()];
    let year = parseInt(m2[3], 10);
    if (year < 100) year += 2000;
    if (mon) return new Date(year, mon - 1, parseInt(m2[1], 10));
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
};

const SortTh = ({ label, field, sortField, sortDir, onSort, className = '' }) => (
  <th
    onClick={() => onSort(field)}
    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider group cursor-pointer select-none hover:bg-gray-100 ${className}`}
  >
    <span className="inline-flex items-center gap-1">
      {label}
      {sortField === field
        ? (sortDir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
        : <ChevronsUpDown className="w-3.5 h-3.5 opacity-30 group-hover:opacity-60" />}
    </span>
  </th>
);

const UnlockingArchived = () => {
  const { hasPermission } = useAuth();
  const canExport = hasPermission('qm:view');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    status: 'all',
    partner: 'all',
    calendarYear: 'all',
    searchQuery: ''
  });
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(1);

  const setFilter = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
    setPage(1);
  };

  useEffect(() => {
    fetchUnlockingData();
  }, []);

  const fetchUnlockingData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const response = await fetch(`${API_BASE_URL}/api/qm/registers/unlocking/stats`, {
        method: 'GET',
        credentials: 'include' // Use HTTPOnly cookies for authentication
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to fetch unlocking data');
      }

      const result = await response.json();
      setStats(result.data);
    } catch (err) {
      console.error('Error fetching unlocking data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredRecords = useMemo(() => {
    if (!stats || !stats.records) return [];
    return stats.records.filter(record => {
      if (filters.status !== 'all') {
        if ((record.status || 'Unknown') !== filters.status) return false;
      }
      if (filters.partner !== 'all') {
        // 現名優先：人名是當時的快照，改名過的人不該變成兩個選項
        // （docs/data-model.md §2）。
        if ((staffNow(record.partnerInvolved, record.partnerInvolvedNow) || 'Unknown') !== filters.partner) return false;
      }
      if (filters.calendarYear !== 'all') {
        const date = parseDMY(record.dateOfChanges);
        const year = date ? String(date.getFullYear()) : 'Unknown';
        if (year !== filters.calendarYear) return false;
      }
      if (filters.searchQuery) {
        const query = filters.searchQuery.toLowerCase();
        const client = (record.client || '').toLowerCase();
        const reason = (record.reasonForChanges || '').toLowerCase();
        const evidence = (record.evidenceOfApproval || '').toLowerCase();
        const remarks = (record.remarks || '').toLowerCase();
        if (!client.includes(query) && !reason.includes(query) && !evidence.includes(query) && !remarks.includes(query)) return false;
      }
      return true;
    });
  }, [stats, filters]);

  const sortedRecords = useMemo(() => {
    if (!sortField) return filteredRecords;
    const dateFields = ['financialYearEnd', 'dateOfAuditReport', 'dateOfArchival', 'dateOfChanges'];
    return [...filteredRecords].sort((a, b) => {
      let aVal = a[sortField] ?? '';
      let bVal = b[sortField] ?? '';
      if (dateFields.includes(sortField)) {
        const aD = parseDMY(aVal) || new Date(0);
        const bD = parseDMY(bVal) || new Date(0);
        return sortDir === 'asc' ? aD - bD : bD - aD;
      }
      aVal = String(aVal).toLowerCase();
      bVal = String(bVal).toLowerCase();
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filteredRecords, sortField, sortDir]);

  // ISO 'YYYY-MM-DD' from the API → display DD-MM-YYYY (string swap, no TZ shift)
  const formatDate = (dateStr) => (dateStr ? toDisplayDate(dateStr) : 'N/A');

  // 一個欄位的去重選項。人名欄（`staffField`）認現名——同一人改名後舊記錄與新
  // 記錄的快照不同，拿快照當選項會列出兩筆。
  const getUniqueValues = (field, staffField = false) => {
    if (!stats || !stats.records) return [];
    const read = (r) => (staffField ? staffNow(r[field], r[`${field}Now`]) : r[field]) || 'Unknown';
    const values = [...new Set(stats.records.map(read))];
    return values.sort();
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-600">Loading unlocking archived files data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <h3 className="text-red-800 font-semibold mb-2">Error Loading Data</h3>
        <p className="text-red-600">{error}</p>
        <button
          onClick={fetchUnlockingData}
          className="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
        <p className="text-yellow-800">No unlocking data available.</p>
      </div>
    );
  }

  const paginated = sortedRecords.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const statusOptions = getUniqueValues('status');
  const partnerOptions = getUniqueValues('partnerInvolved', true);
  const calendarYearOptions = stats && stats.records
    ? [...new Set(stats.records.map(r => {
        const d = parseDMY(r.dateOfChanges);
        return d ? String(d.getFullYear()) : null;
      }).filter(Boolean))].sort().reverse()
    : [];

  return (
    <div className="space-y-6">
      {/* Header — unified tab header (docs/dashboard-ui.md) */}
      <DashboardTabHeader
        description="Register of requests to unlock archived engagement files"
        actions={canExport && (
          <button
            onClick={() => {
              const rows = filteredRecords.map(r => ({
                'Client': r.client || '',
                'Financial Year End': r.financialYearEnd ? formatDate(r.financialYearEnd) : '',
                'Date of Audit Report': r.dateOfAuditReport ? formatDate(r.dateOfAuditReport) : '',
                'Date of Archival': r.dateOfArchival ? formatDate(r.dateOfArchival) : '',
                'Date of Changes': r.dateOfChanges ? formatDate(r.dateOfChanges) : '',
                'Reason for Changes': r.reasonForChanges || '',
                'Evidence of Approval': r.evidenceOfApproval || '',
                'Partner Involved': staffNow(r.partnerInvolved, r.partnerInvolvedNow) || '',
                'Engagement Manager/Director': staffNow(r.engagementManager, r.engagementManagerNow) || '',
                'Status': r.status || '',
                'Remarks': r.remarks || '',
              }));
              exportToExcel(rows, 'Unlocking_Archived_Report.xlsx');
            }}
            className={exportExcelBtnCls}
          >
            <Download className="w-4 h-4" />
            Export Excel
          </button>
        )}
      />

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
          <div className="text-gray-600 text-sm">Total Requests</div>
          <div className="text-3xl font-bold text-gray-900 mt-2">{stats.total}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
          <div className="text-gray-600 text-sm">Approved</div>
          <div className="text-3xl font-bold text-green-600 mt-2">{stats.approved}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-yellow-500">
          <div className="text-gray-600 text-sm">Pending</div>
          <div className="text-3xl font-bold text-yellow-600 mt-2">{stats.pending}</div>
        </div>

        <div className="bg-white rounded-lg shadow p-6 border-l-4 border-purple-500">
          <div className="text-gray-600 text-sm">Approval Rate</div>
          <div className="text-3xl font-bold text-purple-600 mt-2">{stats.approvalRate}%</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search
            </label>
            <input
              type="text"
              value={filters.searchQuery}
              onChange={(e) => setFilter('searchQuery', e.target.value)}
              placeholder="Search client, description, remarks..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilter('status', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Statuses</option>
              {statusOptions.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Partner
            </label>
            <select
              value={filters.partner}
              onChange={(e) => setFilter('partner', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Partners</option>
              {partnerOptions.map(partner => (
                <option key={partner} value={partner}>{partner}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Calendar Year
            </label>
            <select
              value={filters.calendarYear}
              onChange={(e) => setFilter('calendarYear', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Years</option>
              {calendarYearOptions.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-3 text-sm text-gray-600">
          Showing {filteredRecords.length} of {stats.total} requests
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <SortTh label="Client" field="client" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Financial Year End" field="financialYearEnd" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Date of Audit Report" field="dateOfAuditReport" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Date of Archival" field="dateOfArchival" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Date of Changes" field="dateOfChanges" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Reason for Changes" field="reasonForChanges" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Evidence of Approval" field="evidenceOfApproval" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Partner" field="partnerInvolved" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Manager/Director" field="engagementManager" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Status" field="status" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
                <SortTh label="Remarks" field="remarks" sortField={sortField} sortDir={sortDir} onSort={handleSort} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan="11" className="px-6 py-8 text-center text-gray-500">
                    No records match the selected filters
                  </td>
                </tr>
              ) : (
                paginated.map((record, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 text-sm text-gray-900 font-medium">
                      {record.client || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDateShort(record.financialYearEnd) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDateShort(record.dateOfAuditReport) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDateShort(record.dateOfArchival) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                      {formatDateShort(record.dateOfChanges) || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 min-w-[260px] whitespace-pre-wrap break-words">
                      {record.reasonForChanges || 'N/A'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 min-w-[260px] whitespace-pre-wrap break-words">
                      {record.evidenceOfApproval || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700 font-medium">
                      <NowName value={record.partnerInvolved} now={record.partnerInvolvedNow} fallback="N/A" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-700">
                      <NowName value={record.engagementManager} now={record.engagementManagerNow} fallback="N/A" />
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                        record.status && record.status.toLowerCase().includes('approved')
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {record.status || 'Unknown'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 min-w-[260px] whitespace-pre-wrap break-words">
                      {record.remarks || 'N/A'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <TablePager
          page={page}
          setPage={setPage}
          total={sortedRecords.length}
          className="flex items-center justify-between px-6 py-3 border-t border-gray-200 bg-gray-50"
        />
      </div>
    </div>
  );
};

export default UnlockingArchived;
